<template>
  <div class="change-logs">
    <table class="cl-table">
      <colgroup>
        <col style="width: 63px" />   <!-- Serial -->
        <col style="width: 150px" />  <!-- Date -->
        <col style="width: 120px" />  <!-- User -->
        <col style="width: 180px" />  <!-- Change -->
        <col style="width: 180px" />  <!-- Tag -->
        <col style="width: 110px" />  <!-- Action -->
      </colgroup>

      <thead>
        <tr>
          <th>Serial</th>
          <th>Date</th>
          <th>User</th>
          <th>Change</th>
          <th>Tag</th>
          <th>Action</th>
        </tr>
      </thead>

      <tbody>
        <tr
          v-for="log in sortedChangeLogs"
          :key="log.id"
          :class="['change-log-row', { 'change-log-row--tag': isTagChangeLog(log) }]"
          @click="openLogDetails(log)"
        >
          <td v-tooltip="'Click to see details'">{{ log.serial }}</td>
          <td v-tooltip="'Click to see details'">{{ formatDate(log.created_at) }}</td>
          <td v-tooltip="'Click to see details'">{{ log.username }}</td>
          <td v-tooltip="'Click to see details'">{{ formatChange(log) }}</td>
          <td>
            <span
              v-if="isTagChangeLog(log) && getDisplayTagName(log)"
              v-tooltip="getTagDescription(log)"
              class="tag-pill"
            >
              <SVGIcon
                :iconName="'tag'"
                :width="'14px'"
                :height="'14px'"
                class="tag-pill-icon"
              />
              <span class="tag-pill-text">{{ getDisplayTagName(log) }}</span>
            </span>
          </td>
          <td class="action-cell">
            <button
              v-if="canRestoreTag(log)"
              type="button"
              class="restore-button"
              :disabled="restoringTag === getRestoreTagName(log)"
              @click.stop="restoreTag(log)"
            >
              {{ restoringTag === getRestoreTagName(log) ? 'Restoring...' : 'Restore' }}
            </button>
            <button
              v-else-if="canCreateTag(log)"
              type="button"
              class="create-tag-button"
              @click.stop="emit('create-tag')"
            >
              Create Tag
            </button>
          </td>
        </tr>
      </tbody>
    </table>

    <div v-if="loading" class="loading-container">
      <div class="spinner"></div>
    </div>

    <div v-if="!loading && sortedChangeLogs.length === 0" class="empty-state">
      No change logs found
    </div>

    <Modal
      v-model:show="showDetailsModal"
      :title="'Change Log Details'"
      :width="'60%'"
      :height="'70%'"
      :persistent="false"
    >
      <template #modal-main>
        <div class="details-scroll-area">
          <div
            v-for="entry in detailEntries"
            :key="entry.key"
            class="detail-row"
          >
            <div class="detail-key">{{ entry.key }}</div>
            <div class="detail-value">{{ entry.value }}</div>
          </div>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import Modal from '@/components/Modal.vue'
import SVGIcon from '@/components/SVGIcon.vue'
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface'
import { useToast } from 'vue-toast-notification'

const $toast = useToast()
const emit = defineEmits(['restored', 'create-tag'])

const props = defineProps({
  datasetId: {
    type: Number,
    required: true
  }
})

const store = useStore()
const dsConn = new DatastoreConnect(store.state.enterpriseServerUrl)

const changeLogs = ref([])
const loading = ref(false)
const showDetailsModal = ref(false)
const selectedLog = ref(null)
const restoringTag = ref('')

const sortedChangeLogs = computed(() => {
  return [...changeLogs.value].sort((a, b) => {
    return new Date(b.created_at) - new Date(a.created_at)
  })
})

const topLog = computed(() => sortedChangeLogs.value[0] || null)

const detailEntries = computed(() => {
  if (!selectedLog.value) {
    return []
  }

  return Object.entries(selectedLog.value)
    .filter(([, value]) => value !== undefined)
    .map(([key, value]) => ({
      key,
      value: formatDetailValue(value)
    }))
})

onMounted(() => {
  GetChangeLogs()
})

async function GetChangeLogs() {
  loading.value = true
  try {
    const response = await dsConn.api('version.changelog', {
      dataset_id: props.datasetId
    })
    
    if (response?.error) {
      $toast.open({
        queue: true,
        dismissible: true,
        message: `Failed to fetch change logs: ${response.error.message}`,
        type: 'error',
        position: 'top'
      })
      changeLogs.value = []
    } else {
      changeLogs.value = response?.result?.entries || []
    }
  } catch (error) {
    console.error('Error fetching change logs:', error)
    $toast.open({
      queue: true,
      dismissible: true,
      message: `Failed to fetch change logs: ${error}`,
      type: 'error',
      position: 'top'
    })
    changeLogs.value = []
  } finally {
    loading.value = false
  }
}

function formatDate(dateString) {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

function formatChangeData(data) {
  if (!data) return ''
  return JSON.stringify(data, null, 2)
}

function formatChange(log) {
  const entityType = String(log?.entity_type || '').trim().toLowerCase()
  const operation = String(log?.operation || '').trim().toLowerCase()
  if (entityType && operation) {
    return `${entityType}.${operation}`
  }
  return entityType || operation || '-'
}

function getTagName(log) {
  const data = getChangeDataObject(log)
  const restoreData = data?.restore || data?.restored || {}
  const beforeData = data?.before || {}
  const afterData = data?.after || {}
  const entityData = data?.entity || {}

  return log?.tag_name
    || log?.tag
    || log?.tagName
    || log?.tag_value
    || log?.value
    || log?.entity_name
    || log?.entity_value
    || log?.name
    || log?.entity_tag
    || log?.version_tag
    || data?.raw
    || data?.tag_name
    || data?.tag
    || data?.tagName
    || data?.tag_value
    || data?.name
    || data?.value
    || data?.target_value
    || data?.restored_value
    || data?.restored_tag
    || data?.restored_tag_name
    || data?.restored_from_tag
    || data?.restore_tag
    || data?.restore_tag_name
    || data?.target_tag
    || data?.to_tag
    || data?.from_tag
    || data?.new_tag
    || data?.new_name
    || restoreData?.tag
    || restoreData?.name
    || restoreData?.tag_name
    || restoreData?.tag_value
    || beforeData?.tag_name
    || beforeData?.tag
    || beforeData?.name
    || beforeData?.value
    || afterData?.tag_name
    || afterData?.tag
    || afterData?.name
    || afterData?.value
    || entityData?.tag_name
    || entityData?.tag
    || entityData?.name
    || entityData?.value
    || ''
}

function isTagCreateLog(log) {
  return formatChange(log) === 'tag.create'
}

function isTagRestoreLog(log) {
  return formatChange(log) === 'tag.restore'
}

function isTagChangeLog(log) {
  return formatChange(log).startsWith('tag.')
}

function getDisplayTagName(log) {
  const tagName = String(getTagName(log) || '').trim()
  if (!tagName) return ''

  if (!isTagCreateLog(log) && !isTagRestoreLog(log)) {
    return tagName
  }

  try {
    return decodeURIComponent(String(tagName))
  } catch {
    return tagName
  }
}

function getRestoreTagName(log) {
  const rawTagName = String(getTagName(log) || '').trim()
  if (!rawTagName) return ''

  try {
    return decodeURIComponent(rawTagName)
  } catch {
    return rawTagName
  }
}

function canRestoreTag(log) {
  return isTagChangeLog(log) && Boolean(getDisplayTagName(log))
}

function canCreateTag(log) {
  if (!topLog.value || log?.id !== topLog.value?.id) {
    return false
  }

  return !isTagChangeLog(log) || !getDisplayTagName(log)
}

function getTagDescription(log) {
  const data = getChangeDataObject(log)
  return log?.tag_description
    || log?.tagDescription
    || data?.tag_description
    || data?.description
    || data?.after?.description
    || data?.entity?.description
    || log?.description
    || 'No description'
}

function getChangeDataObject(log) {
  const rawData = log?.change_data
  if (!rawData) return {}
  if (typeof rawData === 'object') return rawData

  if (typeof rawData === 'string') {
    try {
      return JSON.parse(rawData)
    } catch {
      // Some changelog entries store a plain scalar (e.g. restored tag name).
      return { raw: rawData, value: rawData, name: rawData, tag: rawData }
    }
  }

  return {}
}

function formatDetailValue(value) {
  if (value === null) return 'null'
  if (typeof value === 'object') return JSON.stringify(value, null, 2)
  return String(value)
}

function openLogDetails(log) {
  selectedLog.value = log
  showDetailsModal.value = true
}

async function restoreTag(log) {
  const tagName = getRestoreTagName(log)
  if (!props.datasetId || !tagName) {
    return
  }

  restoringTag.value = tagName
  try {
    const response = await dsConn.api('version.tag.restore', {
      dataset_id: props.datasetId,
      name: tagName
    })

    if (response?.error) {
      $toast.open({
        queue: true,
        dismissible: true,
        message: `Failed to restore tag: ${response.error.message}`,
        type: 'error',
        position: 'top'
      })
      return
    }

    $toast.open({
      queue: true,
      dismissible: true,
      message: `Restored tag ${tagName}`,
      type: 'success',
      position: 'top'
    })
    await GetChangeLogs()
    emit('restored', tagName)
  } catch (error) {
    console.error('Error restoring tag:', error)
    $toast.open({
      queue: true,
      dismissible: true,
      message: `Failed to restore tag: ${error}`,
      type: 'error',
      position: 'top'
    })
  } finally {
    restoringTag.value = ''
  }
}
</script>

<style lang="scss" scoped>
.change-logs {
  position: relative;
  height: 100%;
  overflow-y: auto;
  font-size: 14px;
}

.create-tag-button {
  border: 1px solid rgba(25, 118, 210, 0.35);
  background: #fff;
  color: #1976d2;
  border-radius: 4px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.create-tag-button:hover {
  background: #1976d2;
  color: #fff;
}

.cl-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

/* Header */
.cl-table thead th {
  position: sticky;
  top: 0;
  background: #fafafa;
  z-index: 2;
  padding: 6px 8px;
  text-align: left;
  border-bottom: 1px solid #ccc;
  font-weight: 600;
}

/* Body */
.cl-table tbody td {
  padding: 6px 8px;
  border-bottom: 1px solid #eee;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cl-table tbody tr:hover {
  background: #f6f6f6;
}

.cl-table tbody tr.change-log-row--tag td {
  background: #eef5ff;
}

.cl-table tbody tr.change-log-row--tag:hover td {
  background: #e5efff;
}

.change-log-row {
  cursor: pointer;
}

.tag-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  padding: 2px 8px;
  border: 1px solid #8d8d95;
  border-radius: 4px;
  background: #ffffff;
  color: #2b2b2b;
  font-size: 13px;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.action-cell {
  width: 110px;
}

.restore-button {
  border: 1px solid rgba(25, 118, 210, 0.35);
  background: #fff;
  color: #1976d2;
  border-radius: 4px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, opacity 0.15s ease;
}

.restore-button:hover:not(:disabled) {
  background: #1976d2;
  color: #fff;
}

.restore-button:disabled {
  opacity: 0.6;
  cursor: wait;
}

.tag-pill-icon {
  flex: 0 0 auto;
}

.tag-pill-text {
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.details-scroll-area {
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: 100%;
  overflow: auto;
  padding: 8px;
  text-align: left;
}

.detail-row {
  display: grid;
  grid-template-columns: minmax(140px, 180px) 1fr;
  gap: 10px;
  align-items: start;
  padding: 4px 0;
  border-bottom: 1px solid #eee;
  justify-items: start;
}

.detail-key {
  font-weight: 600;
  color: #232323;
  word-break: break-word;
  text-align: left;
}

.detail-value {
  white-space: pre-wrap;
  word-break: break-word;
  font-family: monospace;
  text-align: left;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.empty-state {
  text-align: center;
  padding: 40px;
  color: #999;
  font-size: 0.9rem;
}
</style>
